import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface ImpersonationContextType {
  /** The tenant currently being managed (null = super admin home view) */
  actingTenantId: string | null;
  /** Display name of the tenant being managed */
  actingTenantName: string | null;
  /** Start managing a specific tenant */
  startImpersonation: (tenantId: string, tenantName: string) => void;
  /** Stop impersonation and return to super admin view */
  stopImpersonation: () => void;
  /** Check if currently impersonating a tenant */
  isImpersonating: boolean;
  /** Check if user is a super admin (regardless of impersonation state) */
  isSuperAdmin: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'impersonatedTenant';
const IMPERSONATION_NAME_STORAGE_KEY = 'impersonatedTenantName';

export const ImpersonationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [actingTenantId, setActingTenantId] = useState<string | null>(null);
  const [actingTenantName, setActingTenantName] = useState<string | null>(null);

  const isSuperAdmin = user?.type === 'super_admin';

  // Load impersonation state from localStorage on mount
  useEffect(() => {
    if (isSuperAdmin) {
      const storedTenantId = localStorage.getItem(IMPERSONATION_STORAGE_KEY);
      const storedTenantName = localStorage.getItem(IMPERSONATION_NAME_STORAGE_KEY);
      if (storedTenantId) {
        setActingTenantId(storedTenantId);
        setActingTenantName(storedTenantName || storedTenantId);
      }
    }
  }, [isSuperAdmin]);

  // Clear impersonation if user logs out or changes
  useEffect(() => {
    if (!user) {
      setActingTenantId(null);
      setActingTenantName(null);
      localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      localStorage.removeItem(IMPERSONATION_NAME_STORAGE_KEY);
    }
  }, [user]);

  const startImpersonation = (tenantId: string, tenantName: string) => {
    if (!isSuperAdmin) {
      console.warn('Only super admins can impersonate tenants');
      return;
    }
    setActingTenantId(tenantId);
    setActingTenantName(tenantName);
    localStorage.setItem(IMPERSONATION_STORAGE_KEY, tenantId);
    localStorage.setItem(IMPERSONATION_NAME_STORAGE_KEY, tenantName);
  };

  const stopImpersonation = () => {
    setActingTenantId(null);
    setActingTenantName(null);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    localStorage.removeItem(IMPERSONATION_NAME_STORAGE_KEY);
  };

  const isImpersonating = isSuperAdmin && actingTenantId !== null;

  return (
    <ImpersonationContext.Provider
      value={{
        actingTenantId,
        actingTenantName,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
        isSuperAdmin,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};
